import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import "../css/ChemistDetails.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";

const ChemistDetails = () => {
  const [chemists, setChemists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    fromDCSAssigned,
    selectedState,
    selectedEmployee,
    tableData,
    states,
    employees,
  } = location.state || {};

  // Try multiple possible keys and pick first non-null/undefined
  const getField = (obj = {}, keys = []) => {
    for (const k of keys) {
      if (
        Object.prototype.hasOwnProperty.call(obj, k) &&
        obj[k] !== null &&
        obj[k] !== undefined
      ) {
        return obj[k];
      }
    }
    return null;
  };

  // Convert "Yes"/"No"/true/false -> boolean
  const boolValue = (value) => {
    if (value === true) return true;
    if (typeof value === "string") {
      const v = value.trim().toLowerCase();
      if (v === "yes" || v === "true") return true;
    }
    return false;
  };

  // Your earlier formatDate helper (keeps date-only/time as you preferred)
  const formatDate = (value) => {
    if (!value) return "";

    const dt = new Date(value);
    if (isNaN(dt.getTime())) return value;

    const date = dt.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const time = dt.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // return date in first line, time in next line
    return `${date}\n${time}`;
  };

  // Parse lat/long into "lat, newline, long" when possible
  const parseLatLong = (chemist) => {
    // prefer explicit latitude/longitude keys
    const lat = getField(chemist, [
      "latitude",
      "lat",
      "geo_lat",
      "geoLatitude",
    ]);
    const lng = getField(chemist, [
      "longitude",
      "long",
      "lng",
      "geo_lng",
      "geoLongitude",
    ]);
    if (lat !== null && lng !== null) {
      return `${lat},\n${lng}`;
    }

    // fallback to single-field variants like "geo_latlong" or "lat_long"
    const geoLatLong = getField(chemist, [
      "geo_latlong",
      "geo_lat_long",
      "lat_long",
      "geoLatLong",
      "latLong",
      "geo_latlng",
      "geo_lat_lng",
      "geo_latlngs",
    ]);
    if (geoLatLong) {
      if (typeof geoLatLong === "string") {
        const parts = geoLatLong.split(",");
        if (parts.length >= 2) return `${parts[0].trim()},\n${parts[1].trim()}`;
        return geoLatLong;
      }
      if (typeof geoLatLong === "object") {
        const l1 = getField(geoLatLong, ["lat", "latitude"]);
        const l2 = getField(geoLatLong, ["lng", "long", "longitude"]);
        if (l1 !== null && l2 !== null) return `${l1},\n${l2}`;
        try {
          return JSON.stringify(geoLatLong);
        } catch {
          return String(geoLatLong);
        }
      }
      return String(geoLatLong);
    }

    // nothing available
    return null;
  };

  const renderField = (field) => {
    if (field === null || field === undefined) return "";
    if (typeof field === "object") {
      if (field.name) return field.name;
      if (field.id) return field.id;
      try {
        return JSON.stringify(field);
      } catch {
        return String(field);
      }
    }
    return String(field);
  };



  useEffect(() => {
    const fetchChemists = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/chemist/chemists/filter?employee_id=${employeeId}`,
          {
            headers: getAuthHeaders(),
          }
        );
        if (!response.ok)
          throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setChemists(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Error fetching chemists:", e);
        setError(e.message);
        setChemists([]);
      } finally {
        setLoading(false);
      }
    };

    if (employeeId) fetchChemists();
    else setLoading(false);
  }, [employeeId]);

  const handlePrint = () => window.print();
  const handleExportExcel = () => console.log("Exporting to Excel...");

  const handleResetGeo = async (chemist) => {
    console.log("Reset geo for", chemist.id ?? chemist.chemist_code);
    // wire your API here and refresh the row after success
  };

  if (loading)
    return <div className="loading-message">Loading chemist details...</div>;
  if (error)
    return (
      <div className="error-message">
        Error loading chemist details: {error}
      </div>
    );

  return (
    <div className="chemist-main-container">
      <div className="chemist-data-container">
        <div className="center mt-5 flex justify-center p-2">
          <h1 className="chemist-title">
            Chemist Details (employee ID: {employeeId})
          </h1>
        </div>

        <div className="chemist-table-container">
          {chemists.length === 0 ? (
            <div className="no-data-message">
              <h3>No Chemist Data Found</h3>
              <p>No chemists found for employee {employeeId}</p>
            </div>
          ) : (
            <table className="chemist-table">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Code</th>
                  <th>Chemist</th>
                  <th>State</th>
                  <th>City</th>
                  <th>Area</th>
                  <th>Route</th>
                  <th>Mobile</th>
                  <th>Phone</th>
                  <th>Address</th>

                  {/* Updated header: show latitude and longitude under "Geo Mapped" */}
                  <th>Geo Mapped</th>

                  {/* keep the boolean flag but with clear label */}
                  <th>Geo mapped flag</th>
                  <th>Geo mapped date</th>
                  <th>Geo freezed</th>

                  {/* Geo freezed date truncated */}
                  <th>Geo freezed date</th>
                  <th>Reset Geo Code</th>
                </tr>
              </thead>

              <tbody>
                {chemists.map((chemist, index) => {
                  const latLongString = parseLatLong(chemist); // returns "lat,\nlng" or null

                  const geoMappedFlag =
                    getField(chemist, [
                      "geo_mapped",
                      "geomapped",
                      "is_geo_mapped",
                      "geoMapped",
                      "geomappedcode",
                    ]) ??
                    chemist.geo_mapped ??
                    chemist.geomapped;

                  const geoMappedDate =
                    getField(chemist, [
                      "geo_mapped_date",
                      "geomappeddate",
                      "geoMappedDate",
                      "geo_mappeddate",
                      "geo_mapped_on",
                    ]) ??
                    chemist.geo_mapped_date ??
                    chemist.geomappeddate;

                  const geoFreezed =
                    getField(chemist, [
                      "geo_freezed",
                      "is_geo_freezed",
                      "geo_freeze",
                      "geoFreezed",
                      "geo_freezed_flag",
                    ]) ??
                    chemist.geo_freezed ??
                    chemist.is_geo_freezed;

                  const geoFreezedDate =
                    getField(chemist, [
                      "geo_freezed_date",
                      "geo_freezzeddate",
                      "geo_freezeddate",
                      "geoFreezedDate",
                    ]) ??
                    chemist.geo_freezed_date ??
                    chemist.geo_freezeddate;

                  const mobile =
                    getField(chemist, [
                      "mobile",
                      "phone_mobile",
                      "employee_mobile",
                    ]) ?? chemist.mobile;
                  const phone =
                    getField(chemist, ["phone", "landline", "telephone"]) ??
                    chemist.phone;

                  return (
                    <tr key={chemist.id ?? index}>
                      <td>{index + 1}</td>
                      <td>{renderField(chemist.chemist_code)}</td>
                      <td>{renderField(chemist.chemist_name)}</td>
                      <td>
                        {renderField(
                          getField(chemist, ["state_name", "state"]) ??
                            chemist.state_name
                        )}
                      </td>
                      <td>
                        {renderField(
                          getField(chemist, ["city_name", "city"]) ??
                            chemist.city_name
                        )}
                      </td>
                      <td>
                        {renderField(
                          getField(chemist, ["area_name", "area"]) ??
                            chemist.area_name
                        )}
                      </td>
                      <td>
                        {renderField(
                          getField(chemist, ["route_name", "route"]) ??
                            chemist.route_name
                        )}
                      </td>
                      <td>{renderField(mobile)}</td>
                      <td>{renderField(phone)}</td>

                      {/* Address wraps */}
                      <td className="address-cell">
                        {renderField(
                          getField(chemist, ["address", "addr"]) ??
                            chemist.address
                        )}
                      </td>

                      {/* NEW: Geo Mapped column now shows latitude and longitude (preferred keys: latitude & longitude) */}
                      <td style={{ whiteSpace: "pre-line" }}>
                        {latLongString ?? "-"}
                      </td>

                      {/* boolean flag */}
                      <td>{String(boolValue(geoMappedFlag))}</td>

                      {/* mapped date formatted */}
                      <td>{formatDate(geoMappedDate)}</td>

                      <td>{String(boolValue(geoFreezed))}</td>

                      {/* Geo freezed date truncated; full value on hover */}
                      <td
                        style={{
                          whiteSpace: "pre-line",
                          maxWidth: "120px",
                          textAlign: "left",
                        }}
                      >
                        {formatDate(geoFreezedDate)}
                      </td>

                      <td>
                        <button
                          className="reset-btn"
                          onClick={() => handleResetGeo(chemist)}
                        >
                          Reset
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

      
         <div className="mt-4 flex gap-4 justify-center">
              <CustomButton onClick={handlePrint} label="Print" variant="print">
                🖨
              </CustomButton>
              <CustomButton
                onClick={handleExportExcel}
                label="Export"
                variant="export"
              >
                📥
              </CustomButton>
            </div>

      </div>
    </div>
  );
};

export default ChemistDetails;
